import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { fetchAudit, keys } from "@/lib/queries";
import { Card, CardBody, CardHeader, CardTitle } from "@/components/ui/Card";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { Select } from "@/components/ui/Select";
import { Pagination } from "@/components/ui/Pagination";
import { SortableHeader } from "@/components/ui/SortableHeader";
import { useTableState } from "@/lib/useTableState";
import { fmtDate } from "@/lib/format";

interface AuditFilters {
  actor_kind: string;
  event_type: string;
  severity: string;
  date_from: string;
  date_to: string;
}

export const Route = createFileRoute("/_app/audit")({
  loader: ({ context }) =>
    context.queryClient.ensureQueryData({
      queryKey: keys.auditList(20, 0, { by: "occurred_at", dir: "desc" }, {}),
      queryFn: () =>
        fetchAudit({ limit: 20, offset: 0, sort: { by: "occurred_at", dir: "desc" } }),
    }),
  component: AuditPage,
});

// Common event-type values used as a dropdown convenience. The free-text
// fallback below lets operators search anything the dropdown doesn't list.
const EVENT_TYPE_OPTIONS = [
  { value: "ADMIN_LOGIN", label: "Admin login" },
  { value: "ADMIN_LOGIN_FAILED", label: "Admin login failed" },
  { value: "ADMIN_CREATED", label: "Admin created" },
  { value: "ADMIN_STATUS_CHANGED", label: "Admin status changed" },
  { value: "ROLE_ASSIGNED", label: "Role assigned" },
  { value: "ROLE_REVOKED", label: "Role revoked" },
  { value: "USER_STATUS_CHANGED", label: "User status changed" },
  // Values are the audit event_type strings the backoffice emits (the
  // -ED forms), not the RBAC permission names — the audit query matches
  // event_type exactly, so the permission-name forms used here previously
  // (WALLET_FREEZE / WALLET_CREDIT) never matched a row.
  { value: "WALLET_FROZEN", label: "Wallet freeze" },
  { value: "WALLET_UNFROZEN", label: "Wallet unfreeze" },
  { value: "WALLET_CREDITED", label: "Wallet credit" },
  { value: "WALLET_DEBITED", label: "Wallet debit" },
  { value: "RULE_OPEN", label: "Market open" },
  { value: "RULE_CLOSE", label: "Market close" },
  { value: "RULE_RESOLVE", label: "Market resolve" },
  { value: "CONFIG_SET", label: "Config set" },
  // Broker lifecycle (SM-716). An emergency disable is a pause, but it is
  // audited under its own type at CRITICAL so it can be found on its own.
  { value: "BROKER_APPROVED", label: "Broker approved" },
  { value: "BROKER_PAUSED", label: "Broker paused" },
  { value: "BROKER_EMERGENCY_PAUSED", label: "Broker emergency disable" },
  { value: "BROKER_RESUMED", label: "Broker resumed" },
  { value: "BROKER_CLOSING", label: "Broker closure begun" },
  { value: "BROKER_CLOSED", label: "Broker closed" },
  { value: "BROKER_STAFF_CHANGED", label: "Broker staff changed" },
  // SM-987 / SM-984: an operator uploaded or replaced, or removed, a broker's GOLD currency image.
  { value: "BROKER_CURRENCY_IMAGE_SET", label: "Broker currency image changed" },
  { value: "BROKER_CURRENCY_IMAGE_REMOVED", label: "Broker currency image removed" },
  // SM-774: a broker switched on / off the global leaderboard.
  { value: "RANKING_BROKER_VISIBILITY_UPDATED", label: "Broker leaderboard visibility changed" },
];

function AuditPage() {
  const table = useTableState<AuditFilters>({
    defaultSort: { by: "occurred_at", dir: "desc" },
    defaultFilters: { actor_kind: "", event_type: "", severity: "", date_from: "", date_to: "" },
  });
  const filtersForKey = {
    actor_kind: table.filters.actor_kind || undefined,
    event_type: table.filters.event_type || undefined,
    severity: table.filters.severity || undefined,
    date_from: table.filters.date_from || undefined,
    date_to: table.filters.date_to || undefined,
  };
  const events = useQuery({
    queryKey: keys.auditList(table.limit, table.offset, table.sort, filtersForKey),
    queryFn: () =>
      fetchAudit({
        limit: table.limit,
        offset: table.offset,
        sort: table.sort,
        actor_kind: table.filters.actor_kind || undefined,
        event_type: table.filters.event_type || undefined,
        severity: table.filters.severity || undefined,
        date_from: table.filters.date_from || undefined,
        date_to: table.filters.date_to || undefined,
      }),
    refetchInterval: 15_000,
  });
  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-display text-2xl tracking-tight">Audit log</h1>
        <p className="text-sm text-muted">Every operator action is logged here with severity, target, and IP. Used for compliance and post-incident replay.</p>
      </div>
      <div className="flex flex-wrap items-end gap-3">
        <div className="w-40">
          <Select
            placeholder="Actor — any"
            value={table.filters.actor_kind}
            onChange={(e) => table.setFilter("actor_kind", e.target.value)}
            options={[
              { value: "ADMIN", label: "Admin" },
              { value: "USER", label: "User" },
              { value: "SYSTEM", label: "System" },
            ]}
          />
        </div>
        <div className="w-56">
          <Select
            placeholder="Event type — any"
            value={table.filters.event_type}
            onChange={(e) => table.setFilter("event_type", e.target.value)}
            options={EVENT_TYPE_OPTIONS}
          />
        </div>
        <div className="w-40">
          <Select
            placeholder="Severity — any"
            value={table.filters.severity}
            onChange={(e) => table.setFilter("severity", e.target.value)}
            options={[
              { value: "INFO", label: "Info" },
              { value: "WARN", label: "Warn" },
              { value: "ERROR", label: "Error" },
              { value: "CRITICAL", label: "Critical" },
            ]}
          />
        </div>
        <div className="w-44">
          <Input
            type="date"
            placeholder="From"
            value={table.filters.date_from}
            onChange={(e) => table.setFilter("date_from", e.target.value)}
          />
        </div>
        <div className="w-44">
          <Input
            type="date"
            placeholder="To"
            value={table.filters.date_to}
            onChange={(e) => table.setFilter("date_to", e.target.value)}
          />
        </div>
        {table.hasActiveFilters && (
          <Button variant="ghost" onClick={() => table.reset()}>Reset</Button>
        )}
      </div>
      <Card>
        <CardHeader className="border-b border-border"><CardTitle>Events</CardTitle><span className="text-2xs text-muted">{events.data?.page.total ?? 0}</span></CardHeader>
        <CardBody className="p-0">
          <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="border-b border-border bg-surface-2">
              <tr>
                <SortableHeader label="When" field="occurred_at" sort={table.sort} onSortChange={table.setSort} className="px-5" />
                <th className="px-3 py-3 text-left text-2xs font-medium uppercase tracking-wide text-muted">Severity</th>
                <SortableHeader label="Event id" field="event_id" sort={table.sort} onSortChange={table.setSort} />
                <th className="px-3 py-3 text-left text-2xs font-medium uppercase tracking-wide text-muted">Event</th>
                <th className="px-3 py-3 text-left text-2xs font-medium uppercase tracking-wide text-muted">Actor</th>
                <th className="px-3 py-3 text-left text-2xs font-medium uppercase tracking-wide text-muted">Target</th>
                <th className="px-5 py-3 text-left text-2xs font-medium uppercase tracking-wide text-muted">Summary</th>
              </tr>
            </thead>
            <tbody>
              {(events.data?.items ?? []).map((e) => (
                <tr key={e.event_id} className="border-b border-border/60 last:border-0">
                  <td className="px-5 py-2 text-2xs text-muted">{fmtDate(e.occurred_at)}</td>
                  <td className="px-3 py-2"><Badge tone={
                    e.severity === "CRITICAL" || e.severity === "ERROR" ? "danger"
                    : e.severity === "WARN" ? "warning"
                    : "muted"
                  }>{e.severity}</Badge></td>
                  <td className="px-3 py-2 font-mono text-2xs text-muted">{e.event_id}</td>
                  <td className="px-3 py-2 text-xs">{e.event_type}</td>
                  <td className="px-3 py-2 font-mono text-2xs text-muted">{e.actor_email || e.admin_id || "—"}</td>
                  <td className="px-3 py-2 font-mono text-2xs text-muted">{e.target_kind ? `${e.target_kind}:${e.target_id ?? "—"}` : "—"}</td>
                  <td className="px-5 py-2 text-2xs text-muted">{e.summary || ""}</td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
          {events.data && (
            <Pagination
              page={events.data.page}
              currentPage={table.page}
              onPageChange={table.setPage}
              itemLabel="events"
            />
          )}
        </CardBody>
      </Card>
    </div>
  );
}
